import { Configuration, LogLevel } from "@azure/msal-browser";

/**
 * The MSAL client ID from the Entra ID app registration.
 * This MUST be set when VITE_DEV_AUTH_BYPASS=false.
 */
const clientId = import.meta.env.VITE_MSAL_CLIENT_ID || "";
const apiClientId = import.meta.env.VITE_MSAL_API_CLIENT_ID || "";

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: import.meta.env.VITE_MSAL_AUTHORITY || "https://login.microsoftonline.com/common",
    redirectUri: import.meta.env.VITE_MSAL_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "sessionStorage",
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          case LogLevel.Verbose:
            console.debug(message);
            break;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

/**
 * Scopes for login -- includes User.Read for basic profile
 * and the API scope derived from the client ID.
 *
 * The API scope uses the format `api://{clientId}/.default` which
 * must match the backend's expected audience (`api://{KLYRA_AZURE_CLIENT_ID}`).
 */
export const loginRequest = {
  scopes: clientId ? [`api://${clientId}/access_as_user`] : ["User.Read"],
};

/**
 * Scopes for silent token acquisition for API calls.
 * Uses the same `api://{clientId}/.default` scope so the token
 * audience matches what the backend MSAL validator expects.
 */
export const apiTokenRequest = {
  scopes: clientId ? [`api://${apiClientId}/access_as_user`] : [],
};

/**
 * Whether MSAL is properly configured (client ID is set).
 * Used by the auth gate to show a config error instead of
 * attempting a broken login flow.
 */
export const isMsalConfigured = clientId.length > 0;

/**
 * Running behind the Databricks Apps reverse proxy (VITE_DEPLOY_TARGET=databricks_apps).
 * The proxy authenticates the user upstream, so the SPA does no MSAL login and
 * attaches no Bearer token; identity/roles come from the backend /me endpoint.
 */
export const isDatabricksApps = import.meta.env.VITE_DEPLOY_TARGET === "databricks_apps";

/** MSAL is active only when configured AND not behind the Databricks Apps proxy. */
export const isMsalActive = isMsalConfigured && !isDatabricksApps;
